/**
 * Guided tours: the contract and the pure helpers.
 *
 * Ported from the ECM host (`src/components/Tour/tourEvents.ts` + `tourUtils.ts`)
 * so a tour written for one reads the same in the other. A lesson starts a tour
 * by dispatching `console:start-tour`; the runner in the app shell drives it.
 */

export const START_TOUR_EVENT = "console:start-tour";
export const TOUR_FINISHED_EVENT = "console:tour-finished";

export interface TourStep {
  /** Route to open before this step (path + optional query). */
  route?: string | null;
  /** Value of a `data-tour` attribute; omitted = centred popover. */
  target?: string | null;
  title?: string;
  body?: string;
  placement?: "top" | "right" | "bottom" | "left" | string;
  /** `data-tour` of an element to click first (opens a tab, a step or a record). */
  click_before?: string | null;
}

export interface TourDefinition {
  id: string;
  title?: string;
  steps: TourStep[];
  /** Report completion to /api/training (default true). */
  track?: boolean;
}

export interface TourFinishedDetail {
  id: string;
  completed: boolean;
  skippedSteps: number;
}

export const startTour = (tour: TourDefinition): void => {
  window.dispatchEvent(new CustomEvent<TourDefinition>(START_TOUR_EVENT, { detail: tour }));
};

export const escapeHtml = (s: string): string =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** Tour bodies are short text: allow **bold**, `code` and line breaks, nothing else. */
export const renderTourBody = (body: string): string =>
  escapeHtml(body)
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\n{2,}/g, "<br/><br/>")
    .replace(/\n/g, "<br/>");

const selectorFor = (target: string): string => `[data-tour="${CSS.escape(target)}"]`;

const isVisible = (el: Element): boolean => {
  const r = el.getBoundingClientRect();
  if (r.width === 0 || r.height === 0) return false;
  const style = window.getComputedStyle(el);
  return style.visibility !== "hidden" && style.display !== "none";
};

/** Best visible match for an anchor. */
export const findVisible = (target: string): Element | null => {
  const visible = Array.from(document.querySelectorAll(selectorFor(target))).filter(isVisible);
  return visible[0] ?? null;
};

export const waitFor = (target: string, timeout: number, cancelled: () => boolean): Promise<Element | null> =>
  new Promise((resolve) => {
    const start = performance.now();
    const tick = () => {
      if (cancelled()) return resolve(null);
      const el = findVisible(target);
      if (el) return resolve(el);
      if (performance.now() - start > timeout) return resolve(null);
      window.setTimeout(tick, 120);
    };
    tick();
  });

export const samePlace = (route: string, loc: { pathname: string; search: string }): boolean => {
  const [path, query = ""] = route.split("?");
  if (path.replace(/\/+$/, "") !== loc.pathname.replace(/\/+$/, "")) return false;
  if (!query) return true;
  const want = new URLSearchParams(query);
  const have = new URLSearchParams(loc.search);
  for (const [k, v] of want.entries()) if (have.get(k) !== v) return false;
  return true;
};
